// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import API_BASE_URL from '../Config.js';

import toast from 'react-hot-toast';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [user, setUser] = useState(null);

  // Restaura sesión al montar
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        // si había algo corrupto, limpiamos
        localStorage.removeItem('user');
      }
    }
  }, []);

  const login = async (nombreUsuario, contrasena) => {
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombreUsuario, contrasena }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Login Failed:', response.status, errorText);
        return { success: false, message: errorText || 'Credenciales incorrectas' };
      }

      const data = await response.json();

      // Persistir token + usuario
      const { accessToken, userId, username, roles, permisos, sucursalId, sucursalNombre } = data;
      setToken(accessToken);
      const userObj = {
        id: userId,
        username: username,
        roles: Array.isArray(roles) ? roles : [],
        permisos: Array.isArray(permisos) ? permisos : [],
        sucursalId: sucursalId,
        sucursalNombre: sucursalNombre,
      };
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(userObj));

      // Estado en memoria
      setUser(userObj);

      return { success: true };
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      return { success: false, message: 'Error de conexión con el servidor. Intente más tarde.' };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_BASE_URL}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error al registrar logout en el servidor:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
    }
  };

  const handleAuthError = (response) => {
    if (response.status === 401) {
      logout();
      toast.error('Sesión expirada o inválida. Por favor, reingrese al sistema.', { id: 'auth-error' });
      return true;
    }
    if (response.status === 403) {
      toast.error('Acceso denegado: No cuenta con los privilegios necesarios.', { id: 'auth-error' });
      return true;
    }
    return false;
  };

  const hasRole = (role) => user?.roles?.includes(role);

  const hasPermission = (permission) => {
    if (hasRole('ADMIN')) return true;
    if (!user?.permisos) return false;
    
    // Si los permisos son un array de objetos (nuevo modelo)
    if (user.permisos.length > 0 && typeof user.permisos[0] === 'object') {
      return user.permisos.some(p => p.nombre === permission);
    }
    
    // Si son strings (modelo antiguo/JWT fallback)
    return user.permisos.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        login,
        logout,
        handleAuthError,
        isAuthenticated: !!token, // clave: usar token
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
